import { useApolloClient, useQuery } from '@apollo/client';
import { noop } from 'lodash';
import { useEffect, useMemo } from 'react';

import type { MyProfileQueryData } from '@skippay/core/@types/myProfile';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import useUserCountryRelatedInfo from '@skippay/core/hooks/useUserCountryRelatedInfo';

import { useFeatureIsEnabled } from 'components/IfFeatureEnabled/useFeatureIsEnabled';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';

import { evaluateBnplLimitsOffer } from './bnplLimitsOffer/evaluate';
import { useJarabotFeatureIsEnabled } from './featureFlag';
import { evaluateInsuranceOffer } from './insuranceOffer/evaluate';
import { JARABOT_DELAYS, JARABOT_STORAGE_KEYS, jarabotClient } from './jarabotClient';
import { useJarabotTracking } from './JarabotTrackingProvider';
import { pickLoginOffer } from './loginOffers/pickLoginOffer';
import type { JarabotLoginOfferEvaluator } from './loginOffers/types';
import { useJarabotWidgetReady } from './useJarabotWidgetReady';
import { getFlag, getJarabotDelay, setFlag } from './utils';

const DELAY = getJarabotDelay(JARABOT_STORAGE_KEYS.LOGIN_OFFER_DELAY, JARABOT_DELAYS.LOGIN_OFFER);
const SESSION_KEY_PREFIX = 'jarabot_login_offer_';

/**
 * Proactive Jarabot offers evaluated once per logged-in session. Only the first applicable
 * offer is sent, in this priority order:
 *   1. BNPL limits contracting (CCD2)
 *   2. Insurance (upsell services)
 */
export function useJarabotLoginOffers(): void {
	const client = useApolloClient();
	const isJarabotEnabled = useJarabotFeatureIsEnabled();
	const { isFeatureEnabled: isBnplLimitsEnabled } = useFeatureIsEnabled('bnplLimitsMinimal');
	const { isFeatureEnabled: isUpsellServicesEnabled } = useFeatureIsEnabled('upsellServices');
	const { country } = useInitialCountryLocale();
	const { selfCareUrl } = useUserCountryRelatedInfo(country);
	const isWidgetReady = useJarabotWidgetReady();
	const { trackJarabotDropoff } = useJarabotTracking();

	const evaluators = useMemo(
		() =>
			[
				isBnplLimitsEnabled && evaluateBnplLimitsOffer,
				isUpsellServicesEnabled && evaluateInsuranceOffer,
			].filter((evaluator): evaluator is JarabotLoginOfferEvaluator => !!evaluator),
		[isBnplLimitsEnabled, isUpsellServicesEnabled]
	);
	const canEvaluate = isJarabotEnabled && evaluators.length > 0;

	const { data: profileData } = useQuery<MyProfileQueryData>(MyProfileQuery, {
		fetchPolicy: 'cache-first',
		skip: !canEvaluate,
	});
	const customerId = profileData?.myProfile?.id;

	useEffect(() => {
		if (!canEvaluate || country !== 'CZ' || !isWidgetReady || !customerId) return undefined;

		const sessionKey = `${SESSION_KEY_PREFIX}${customerId}`;
		if (getFlag(sessionKey)) return undefined;

		let isCancelled = false;

		const timeout = setTimeout(() => {
			pickLoginOffer(
				evaluators,
				{ client, customerId, profile: profileData.myProfile, selfCareUrl, now: new Date() },
				() => isCancelled
			)
				.then((offer) => {
					// Cancelled before a decision: leave the session open so the next run evaluates again.
					if (isCancelled) return;

					setFlag(sessionKey);

					if (!offer) return;

					jarabotClient.sendMessage(offer.event, offer.payload, true, { shake: true });
					trackJarabotDropoff(offer.event, offer.screenName);
					offer.onSent();
				})
				.catch(noop);
		}, DELAY);

		return () => {
			isCancelled = true;
			clearTimeout(timeout);
		};
	}, [
		canEvaluate,
		country,
		isWidgetReady,
		customerId,
		selfCareUrl,
		client,
		evaluators,
		trackJarabotDropoff,
	]);
}
